import React, { useEffect, useMemo, useState } from "react";
import { Modal, Skeleton, Tabs } from "antd";
import styled from "styled-components";
import ListView from "./ListView";
import DetailsView from "./DetailsView";
import { getUserItineraryDetails } from "../../Services/user_question_history";
import { useQuery } from "@tanstack/react-query";

const StyledModal = styled(Modal)`
  .ant-modal-body {
    height: calc(95vh - 100px);
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .ant-modal-title {
    font-size: 14px;
    font-weight: 600;
  }

  .ant-tabs-content-holder {
    flex-grow: 1;
    overflow-y: auto;
  }

  .ant-tabs-nav {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    /* border-bottom: 1px solid #70667c; */
  }

  /* Purple border for each tab */
  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    border: 1px solid #70667c;
    margin-right: 2px;
  }

  /* Active tab with solid purple background (optional) */
  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
    background-color: #f3e8ff; /* light purple background */
    border-bottom-color: #fff !important;
  }
  /* .ant-tabs-card > .ant-tabs-content {
    border-top: 1px solid purple;
  } */
`;

function ViewModal({ isModalVisible, handleCancel, initialValues }) {
  const [renderKey, setRenderKey] = useState(Math.random());
  const [activeTabKey, setActiveTabKey] = useState("1");

  useEffect(() => {
    setRenderKey(Math.random());
    setActiveTabKey("1");
  }, [isModalVisible]);

  const { data, isLoading } = useQuery({
    queryKey: [
      `get_user_itinerary_details-${initialValues?.id}-${initialValues?.user_id}`,
    ],
    queryFn: () =>
      getUserItineraryDetails({
        user_id: initialValues?.user_id,
        itineary_id: initialValues?.id,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!initialValues?.id,
  });

  const {
    simpleData,
    isSimplePlan,
    advancedData,
    isAdvancedPlan,
    essentialDataAdvance,
    essentialDataSimple,
    jsonStructureAdvance,
    jsonStructuresimple,
  } = useMemo(() => {
    let simpleData = [];
    let advancedData = [];
    let isSimplePlan = false;
    let isAdvancedPlan = false;
    let essentialDataSimple = {};
    let essentialDataAdvance = {};
    let jsonStructuresimple = {};
    let jsonStructureAdvance = {};

    const extractPlans = (response_json) => {
      if (!response_json) return { data: [], isPlan: false };

      if (Array.isArray(response_json.plan)) {
        return { data: response_json.plan, isPlan: true };
      }

      const flattened = [];
      const firstKey = Object.keys(response_json)[0];

      if (Array.isArray(response_json[firstKey])) {
        response_json[firstKey].forEach((item) => {
          flattened.push({
            ...item,
            __type: firstKey,
          });
        });
      }

      return { data: flattened, isPlan: false };
    };

    const openaiResponses = data?.openai_response;

    if (Array.isArray(openaiResponses)) {
      openaiResponses.forEach((res) => {
        const { data: plans, isPlan } = extractPlans(res?.response_json);
        if (res?.response_type === "simple") {
          simpleData.push(...plans);
          jsonStructuresimple = res?.response_json;
          isSimplePlan = isSimplePlan || isPlan; // If any is from plan, mark it
          if (initialValues?.category_slug === "essentials") {
            essentialDataSimple = res?.response_json;
          }
        } else if (res?.response_type === "advanced") {
          jsonStructureAdvance = res?.response_json;
          advancedData.push(...plans);
          isAdvancedPlan = isAdvancedPlan || isPlan;
          if (initialValues?.category_slug === "essentials") {
            essentialDataAdvance = res?.response_json;
          }
        }
      });
    }

    return {
      simpleData,
      isSimplePlan,
      advancedData,
      isAdvancedPlan,
      jsonStructureAdvance,
      jsonStructuresimple,
      essentialDataAdvance,
      essentialDataSimple,
    };
  }, [data, initialValues]);

  const items = [];
  let keyCounter = 1;

  if (simpleData?.length) {
    items.push({
      key: String(keyCounter++),
      label: "Basic OpenAI response",
      children: (
        <ListView
          data={simpleData}
          isPlan={
            isSimplePlan || initialValues?.category_slug === "corporate_travel"
          }
          category_slug={initialValues?.category_slug}
          essentialData={essentialDataSimple}
          jsonStructure={jsonStructuresimple}
          category_name={initialValues?.category_name}
          initialValues={initialValues}
          renderKey={renderKey}
        />
      ),
    });
  }

  if (advancedData?.length) {
    items.push({
      key: String(keyCounter++),
      label: "Advance OpenAI response",
      children: (
        <ListView
          data={advancedData}
          isPlan={
            isAdvancedPlan ||
            initialValues?.category_slug === "corporate_travel"
          }
          category_slug={initialValues?.category_slug}
          essentialData={essentialDataAdvance}
          jsonStructure={jsonStructureAdvance}
          category_name={initialValues?.category_name}
          initialValues={initialValues}
          renderKey={renderKey}
        />
      ),
    });
  }

  // Always show Query Details
  items.push({
    key: String(keyCounter++),
    label: "Query Details",
    children: (
      <DetailsView
        data={data?.display_text}
        category_name={initialValues?.category_name}
        initialValues={initialValues}
        imageBase={data?.file_base_url}
      />
    ),
  });

  return (
    <StyledModal
      title={
        <div>
          <p style={{ fontSize: "14px" }}>History Details</p>
          <small style={{ fontSize: "13px" }}>
            {initialValues?.user_name} {"- #"}
            {initialValues?.id}
          </small>
        </div>
      }
      open={isModalVisible}
      footer={null}
      onCancel={handleCancel}
      width={"80vw"}
      style={{ top: 10 }}
      maskClosable={false}
    >
      {isLoading && (
        <Skeleton
          active={isLoading}
          style={{ width: "100%", height: "400px" }}
        />
      )}
      {!isLoading && (
        <Tabs
          type="card"
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          items={items}
        />
      )}
    </StyledModal>
  );
}

export default ViewModal;
